import {
    IconBook2,
    IconBuildingCommunity,
    IconBulb,
    IconHeartHandshake,
    IconMusic,
    IconShirt,
    IconUsersGroup,
} from '@tabler/icons-react';
import cx from 'classnames';
import { ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import styles from './AddEntityInfoCards.module.scss';

type ExampleGroup = {
    icon: ReactNode;
    iconClassName: string;
    title: string;
    description: string;
};

const AddEntityInfoCards = ({
    className,
}: {
    className?: string;
}) => {
    const intl = useIntl();

    const steps = [
        intl.formatMessage(messages.stepOpenGroups),
        intl.formatMessage(messages.stepChooseJoin),
        intl.formatMessage(messages.stepEnterCode),
        intl.formatMessage(messages.stepBecomeMember),
    ];

    const exampleGroups: ExampleGroup[] = [
        {
            icon: <IconShirt size={24} stroke={1.8} />,
            iconClassName: styles.blueIcon,
            title: intl.formatMessage(messages.altarServers),
            description: intl.formatMessage(messages.altarServersDescription),
        },
        {
            icon: <IconMusic size={24} stroke={1.8} />,
            iconClassName: styles.purpleIcon,
            title: intl.formatMessage(messages.parishChoir),
            description: intl.formatMessage(messages.parishChoirDescription),
        },
        {
            icon: <IconBook2 size={24} stroke={1.8} />,
            iconClassName: styles.orangeIcon,
            title: intl.formatMessage(messages.readers),
            description: intl.formatMessage(messages.readersDescription),
        },
        {
            icon: <IconHeartHandshake size={24} stroke={1.8} />,
            iconClassName: styles.pinkIcon,
            title: intl.formatMessage(messages.caritas),
            description: intl.formatMessage(messages.caritasDescription),
        },
        {
            icon: <IconBulb size={24} stroke={1.8} />,
            iconClassName: styles.cyanIcon,
            title: intl.formatMessage(messages.rosary),
            description: intl.formatMessage(messages.rosaryDescription),
        },
        {
            icon: <IconBuildingCommunity size={24} stroke={1.8} />,
            iconClassName: styles.grayIcon,
            title: intl.formatMessage(messages.customGroup),
            description: intl.formatMessage(messages.customGroupDescription),
        },
    ];

    const independentGroups: ExampleGroup[] = [
        {
            icon: <IconUsersGroup size={24} stroke={1.8} />,
            iconClassName: styles.greenIcon,
            title: intl.formatMessage(messages.schoolTrip),
            description: intl.formatMessage(messages.schoolTripDescription),
        },
    ];

    return (
        <aside className={cx(styles.wrapper, className)}>
            <section className={styles.joinCard}>
                <div className={styles.joinHeader}>
                    <h3 className={styles.title}>{intl.formatMessage(messages.joinTitle)}</h3>
                    <IconUsersGroup className={styles.joinIcon} size={38} stroke={1.8} />
                </div>
                <ol className={styles.steps}>
                    {steps.map((step, index) => (
                        <li key={step} className={styles.step}>
                            <span className={styles.stepNumber}>{index + 1}</span>
                            <span className={styles.stepText}>{step}</span>
                        </li>
                    ))}
                </ol>
            </section>

            <section className={styles.examplesCard}>
                <h3 className={styles.title}>{intl.formatMessage(messages.examplesTitle)}</h3>
                <h4 className={styles.sectionSubtitle}>{intl.formatMessage(messages.parishGroupsTitle)}</h4>
                <ul className={styles.exampleList}>
                    {exampleGroups.map((group) => (
                        <li key={group.title} className={styles.exampleItem}>
                            <span className={`${styles.exampleIcon} ${group.iconClassName}`}>{group.icon}</span>
                            <span className={styles.exampleContent}>
                                <span className={styles.exampleTitle}>{group.title}</span>
                                <span className={styles.exampleDescription}>{group.description}</span>
                            </span>
                        </li>
                    ))}
                </ul>
                <h4 className={styles.sectionSubtitle}>{intl.formatMessage(messages.independentGroupsTitle)}</h4>
                <ul className={styles.exampleList}>
                    {independentGroups.map((group) => (
                        <li key={group.title} className={styles.exampleItem}>
                            <span className={`${styles.exampleIcon} ${group.iconClassName}`}>{group.icon}</span>
                            <span className={styles.exampleContent}>
                                <span className={styles.exampleTitle}>{group.title}</span>
                                <span className={styles.exampleDescription}>{group.description}</span>
                            </span>
                        </li>
                    ))}
                </ul>
            </section>
        </aside>
    );
};

const messages = defineMessages({
    joinTitle: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.joinTitle',
        defaultMessage: 'Jak dołączyć do grupy?',
    },
    stepOpenGroups: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.stepOpenGroups',
        defaultMessage: 'Parafianin wchodzi w zakładkę „Grupy” w aplikacji.',
    },
    stepChooseJoin: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.stepChooseJoin',
        defaultMessage: 'Wybiera opcję „Dołącz do grupy”.',
    },
    stepEnterCode: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.stepEnterCode',
        defaultMessage: 'Wpisuje kod dołączenia grupy.',
    },
    stepBecomeMember: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.stepBecomeMember',
        defaultMessage: 'Zostaje członkiem grupy.',
    },
    examplesTitle: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.examplesTitle',
        defaultMessage: 'Przykładowe grupy',
    },
    parishGroupsTitle: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.parishGroupsTitle',
        defaultMessage: 'Grupy parafialne',
    },
    independentGroupsTitle: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.independentGroupsTitle',
        defaultMessage: 'Grupy niezależne',
    },
    altarServers: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.altarServers',
        defaultMessage: 'Ministranci',
    },
    altarServersDescription: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.altarServersDescription',
        defaultMessage: 'Służba liturgiczna przy ołtarzu',
    },
    parishChoir: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.parishChoir',
        defaultMessage: 'Schola Parafialna',
    },
    parishChoirDescription: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.parishChoirDescription',
        defaultMessage: 'Oprawa muzyczna liturgii',
    },
    readers: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.readers',
        defaultMessage: 'Lektorzy',
    },
    readersDescription: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.readersDescription',
        defaultMessage: 'Posługa czytania Słowa Bożego',
    },
    caritas: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.caritas',
        defaultMessage: 'Caritas Parafialna',
    },
    caritasDescription: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.caritasDescription',
        defaultMessage: 'Posługa miłosierdzia i pomoc potrzebującym.',
    },
    rosary: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.rosary',
        defaultMessage: 'Róże Różańcowe',
    },
    rosaryDescription: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.rosaryDescription',
        defaultMessage: 'Wspólna modlitwa różańcowa',
    },
    customGroup: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.customGroup',
        defaultMessage: 'Inna wspólnota lub grupa',
    },
    customGroupDescription: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.customGroupDescription',
        defaultMessage: 'Dostosuj do potrzeb parafii',
    },
    schoolTrip: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.schoolTrip',
        defaultMessage: 'Wycieczka szkolna',
    },
    schoolTripDescription: {
        id: 'src.Screens.Dashboard.AllEntities.AddEntity.AddEntityInfoCards.schoolTripDescription',
        defaultMessage: 'Grupa organizowana poza strukturą parafii',
    },
});

export default AddEntityInfoCards;
